import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import ProgressBar from 'pages/HealthCenter/Report/BrainFunctionalConnectivity/Elements/components/ProgressBar'
import TitlePositionV1 from 'pages/HealthCenter/Report/BrainFunctionalConnectivity/Elements/components/TitlePositionV1'
import DescriptionPosition from 'pages/HealthCenter/Report/BrainFunctionalConnectivity/Elements/components/DescriptionPosition'
import ProcessArrow from 'pages/HealthCenter/Report/BrainFunctionalConnectivity/Elements/components/ProcessArrow'
import useHealthCenterStyles from 'pages/HealthCenter/Report/BrainFunctionalConnectivity/Elements/PercentBarCardV3/style'
import {SCORE_HEALTH} from 'constants/HealthCenterConstant'
import useLangEn from 'hooks/useLangEn'

interface Props {
  headerName: string
  percentage: number
  index: number
  top: string
  middle: string
  bottom: string
  score?: {
    low: number
    high: number
  }
}

function PercentBarCardV3({
  headerName,
  percentage,
  index,
  top,
  middle,
  bottom,
  score = SCORE_HEALTH,
}: Readonly<Props>) {
  const classes = useHealthCenterStyles()
  const {t} = useTranslation()
  const isEn = useLangEn()
  const listPosition = {
    TOP_LEFT: 1,
    TOP_RIGHT: 2,
    BOT_CENTER: 3,
  }
  const contentTitlePosition = [
    {
      id: 0,
      text: bottom,
    },
    {
      id: 1,
      text: middle,
    },
    {
      id: 2,
      text: top,
    },
  ]

  const getTextInRound = useMemo(() => {
    if (Math.floor(percentage) < score.low) {
      return bottom
    }
    if (Math.floor(percentage) > score.high) {
      return top
    }
    return middle
  }, [percentage, t, score])

  const getColorInRound = useMemo(() => {
    if (Math.floor(percentage) < score.low) {
      return '#FF0404'
    }
    if (Math.floor(percentage) > score.high) {
      return '#008BC9'
    }
    return '#B4B4B4'
  }, [percentage, score])

  const checkPosition = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.positionTopLet
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.positionTopRight
    }

    return classes.positionBotLeft
  }, [index])

  const checkTitlePosition = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.titlePositionLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.titlePositionRight
    }

    return ''
  }, [index])

  const checkPercentBarCard = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return `${classes.percentBarCardLeft}`
    }
    if (index === listPosition.TOP_RIGHT) {
      return `${classes.percentBarCardRight}`
    }

    return ''
  }, [index, isEn])

  const checkProcessBar = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.progressBarLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.progressBarRight
    }

    return ''
  }, [index])

  const checkProcessArrow = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.progressArrowLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.progressArrowRight
    }

    return ''
  }, [index])

  const getBorderlineDot = useMemo(() => {
    if (Math.floor(percentage) < score.low) {
      return '1px dotted #FF0404'
    }
    if (Math.floor(percentage) > score.high) {
      return '1px dotted #008BC9'
    }
    return '#B4B4B4 dotted 1px '
  }, [percentage, score])

  const getBackGroundColor = useMemo(() => {
    if (Math.floor(percentage) < score.low) {
      return '#EE3D3B solid 2px'
    }
    if (Math.floor(percentage) > score.high) {
      return '#20A2DC solid 2px'
    }
    return ''
  }, [percentage, score])

  const getColorText = useMemo(() => {
    if (Math.floor(percentage) < score.low) {
      return '#FF0404'
    }
    if (Math.floor(percentage) > score.high) {
      return '#008BC9'
    }
    return ''
  }, [percentage, score])

  return (
    <div className={`${checkPosition} ${classes.connectBrainRegionPart2} `}>
      <DescriptionPosition
        text={getTextInRound}
        index={index}
        getColorInRound={getColorInRound}
        headerName={headerName}
        getColorText={getColorText}
        getBorderlineDot={getBorderlineDot}
      />

      <div
        className={`${classes.percentBarCard} ${checkPercentBarCard}`}
        style={{
          border: getBackGroundColor,
        }}
      >
        <div className={classes.topScorePercent} style={{color: getColorText}}>
          <span
            className={classes.topScorePercentScore}
            style={{color: getColorText}}
          >
            {percentage}
          </span>
          {!isEn && t('IPoints')}
        </div>
        <div
          className={
            index === 3 ? classes.percentBarV2Bot : classes.percentBarV2Top
          }
        >
          <div className={checkProcessArrow}>
            <ProcessArrow index={index} percentage={percentage} />
          </div>

          <div className={checkProcessBar}>
            <ProgressBar index={index} score={score} />
          </div>
          <div className={checkTitlePosition}>
            <TitlePositionV1
              contentTitlePosition={contentTitlePosition}
              index={index}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PercentBarCardV3
